import { call, put, select, takeLeading, all } from 'redux-saga/effects';
import axios from '../axiosConfig';

import {
  resendEmailRequest,
  resendEmailSuccess,
  resendEmailFailure,
  sendClientCopyToClientsRequest,
  sendClientCopyToClientsSuccess,
  sendClientCopyToClientsFailure,
  sendAgentCopyRequest,
  sendAgentCopySuccess,
  sendAgentCopyFailure,
  sendCancellationEmailRequest,
  sendCancellationEmailSuccess,
  sendCancellationEmailFailure,
} from '../../slices/trip';

function* makeRequest(config, successAction, failureAction, errorMessageProperty, payload) {
  try {
    yield call(axios, config);
    yield put(successAction(payload));
  } catch (error) {
    let errorMessage = 'Failed to send email. Please try again later.';
    if (error && error.response && error.response.data) {
      const { title, detail } = error.response.data;
      errorMessage = detail || title || errorMessage;
    }
    const errorElement = payload;
    errorElement[errorMessageProperty] = errorMessage;
    yield put(failureAction(errorElement));
  }
}

function* handleEmailRequest({
  payload,
  successAction,
  failureAction,
  queryParams,
  errorMessageProperty,
  isCancellation = false,
  deriveRecipientsFromBooking = false,
}) {
  const { email } = yield select((store) => store.user);

  let url = '/Email/trip';
  if (payload.brochureId) url = '/brochure/send';
  else if (payload.compareId) url = '/compare/send';
  else if (isCancellation) url = '/Email/sendcancellationemail';

  const doDeriveRecipientsFromBooking =
    deriveRecipientsFromBooking && !payload.brochureId && !payload.compareId;

  const dataPayload = {
    toEmail: doDeriveRecipientsFromBooking
      ? undefined
      : (queryParams || {}).toEmail
        ? queryParams.toEmail
        : email,
    tripId: payload.tripId,
    tripSegmentId: payload.tripSegmentId,
    brochureId: payload.brochureId,
    compareId: payload.compareId,
    ...payload,
    ...(queryParams || {}),
  };
  const dataWithoutNulls = Object.fromEntries(
    Object.entries(dataPayload || {}).filter(([, v]) => v != null),
  );

  const config = {
    method: 'post',
    url,
    data: { ...dataWithoutNulls },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };

  yield call(makeRequest, config, successAction, failureAction, errorMessageProperty, payload);
}

// "Send Client Copy" sends the client-formatted email to the logged-in agent (self-copy for review).
function* resendEmailWorker(action) {
  yield call(handleEmailRequest, {
    payload: action.payload,
    successAction: resendEmailSuccess,
    failureAction: resendEmailFailure,
    errorMessageProperty: 'sendClientCopyEmailError',
    queryParams: { doSendClientCopyEmail: true },
  });
}

// "Send Client Copy to Clients" sends to every stored booking recipient (derived from the booking).
function* sendClientCopyToClientsWorker(action) {
  yield call(handleEmailRequest, {
    payload: action.payload,
    successAction: sendClientCopyToClientsSuccess,
    failureAction: sendClientCopyToClientsFailure,
    errorMessageProperty: 'sendClientCopyToClientsError',
    queryParams: { doSendClientCopyEmail: true },
    deriveRecipientsFromBooking: true,
  });
}

function* sendAgentCopyWorker(action) {
  yield call(handleEmailRequest, {
    payload: action.payload,
    successAction: sendAgentCopySuccess,
    failureAction: sendAgentCopyFailure,
    errorMessageProperty: 'sendAgentCopyEmailError',
    queryParams: { doSendAgentCopyEmail: true },
  });
}

function* sendCancellationEmailWorker(action) {
  yield call(handleEmailRequest, {
    payload: action.payload,
    successAction: sendCancellationEmailSuccess,
    failureAction: sendCancellationEmailFailure,
    errorMessageProperty: 'cancellationEmailError',
    isCancellation: true,
  });
}

function* resendEmailWatcher() {
  yield takeLeading(resendEmailRequest.toString(), resendEmailWorker);
}

function* sendClientCopyToClientsWatcher() {
  yield takeLeading(sendClientCopyToClientsRequest.toString(), sendClientCopyToClientsWorker);
}

function* sendAgentCopyWatcher() {
  yield takeLeading(sendAgentCopyRequest.toString(), sendAgentCopyWorker);
}

function* sendCancellationEmailWatcher() {
  yield takeLeading(sendCancellationEmailRequest.toString(), sendCancellationEmailWorker);
}

export default function* emailRequestWatcher() {
  yield all([
    resendEmailWatcher(),
    sendClientCopyToClientsWatcher(),
    sendAgentCopyWatcher(),
    sendCancellationEmailWatcher(),
  ]);
}
